/**
 * Utilidad comunitaria para comprimir imágenes del lado del cliente en el Frontend.
 * Incluye funcionalidad para estampar metadatos (watermark) en la fotografía.
 */

export const resizeAndCompressImage = (file, watermarkData = null, minKb = 300, maxKb = 600, maxWidth = 1200) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target.result;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;

                // 1. Redimensionar si es mayor al máximo permitido (Ej: 1200px)
                if (width > maxWidth) {
                    height = Math.round((height * maxWidth) / width);
                    width = maxWidth;
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);

                // --- ESTAMPADO DE MARCA DE AGUA (WATERMARK) ---
                if (watermarkData) {
                    // Configuración del texto
                    const fontSize = Math.max(16, Math.floor(width * 0.03)); // Tamaño dinámico según resolución
                    ctx.font = `bold ${fontSize}px sans-serif`;
                    const margin = 20;

                    // Preparar textos
                    // Ej formateo fecha: "11/03/2026 14:30:00"
                    const now = new Date();
                    const fechaHora = now.toLocaleString('es-CL', { 
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        hour: '2-digit', minute: '2-digit', second: '2-digit'
                    });
                    
                    const lineas = [
                        `FECHA/HORA: ${fechaHora}`,
                        `TÉCNICO: ${watermarkData.tecnicoNombre || 'Desconocido'}`,
                        `CLIENTE: ${watermarkData.codCliente || 'N/A'}`,
                        `COMUNA: ${watermarkData.comuna || 'N/A'}`
                    ];

                    // Dibujar fondo semi-transparente para que el texto sea legible
                    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
                    const textHeights = lineas.length * (fontSize + 5);
                    const rectWidth = width - (margin * 2);
                    // Posicionamos el bloque en la parte inferior de la imagen
                    const rectY = height - margin - textHeights - 10;
                    ctx.fillRect(margin - 10, rectY, rectWidth + 20, textHeights + 20);

                    // Esculpir texto en blanco y naranja alternado
                    let currentY = rectY + fontSize + 5;
                    lineas.forEach((linea, index) => {
                        ctx.fillStyle = index === 0 ? '#ffc107' : '#ffffff'; // Destacar la fecha en amarillo
                        ctx.fillText(linea, margin, currentY);
                        currentY += fontSize + 5;
                    });
                }
                // --- FIN WATERMARK ---

                // 2. Comprimir iterativamente para alcanzar el rango deseado (300kb - 600kb)
                let tempQuality = 0.9; // Empezamos en un 90%
                let compressedFile = null;
                
                // Función recursiva para buscar el peso ideal (simulado para este boilerplate rápido)
                const checkSize = (quality) => {
                    const dataUrl = canvas.toDataURL('image/jpeg', quality);
                    
                    // Cálculo aproximado de los bytes desde base64: (longitud String * 3/4)
                    const tempSizeBytes = Math.round((dataUrl.length * 3) / 4);
                    const tempKb = tempSizeBytes / 1024;

                    // Si estamos por encima de los 600kb y todavía podemos reducir la calidad
                    if (tempKb > maxKb && quality > 0.3) {
                        checkSize(quality - 0.1); 
                    // Si ya estamos por debajo del máximo permitido, o alcanzamos calidad mínima
                    } else {
                        // Lo convertimos en Blob/File real para mandarlo al servidor
                        canvas.toBlob((blob) => {
                            if (!blob) {
                                reject(new Error('Canvas to Blob failed'));
                                return;
                            }
                            
                            // Log opcional para verificar la meta
                            // console.log(`Compresión finalizada: Calidad: ${quality.toFixed(2)}, Tamaño: ${tempKb.toFixed(1)} kb`);
                            
                            // Reconstruimos a un objeto tipo File para mantener compatibilidad con FormData
                            compressedFile = new File([blob], file.name, {
                                type: 'image/jpeg',
                                lastModified: Date.now(),
                            });
                            
                            resolve({
                                file: compressedFile,
                                previewUrl: URL.createObjectURL(blob),
                                sizeKb: tempKb,
                            });
                            
                        }, 'image/jpeg', quality);
                    }
                };

                // Iniciamos la compresión
                checkSize(tempQuality);
            };
            img.onerror = (error) => reject(error);
        };
        reader.onerror = (error) => reject(error);
    });
};
